import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from '@angular/material/dialog';
import { ErrorComponent } from "./modals/error/error.component";
import { AppConstants } from "./constants/app-constants";
import { AppLocalStorage } from "./utils/local-storage";
import { MatSnackBar } from '@angular/material/snack-bar';

const logout = (router: Router, snackBar: MatSnackBar) => {
  snackBar.open('Your session has expired. Please log in again.', 'Close', {
    duration: 7000,
    horizontalPosition: 'center',
    verticalPosition: 'top',
  });
  router.navigate(['/']);
  AppLocalStorage.logout();
};

let isDialogOpened = false;

const handleError = (error: HttpErrorResponse, dialog: MatDialog) => {
  const excludedWords = ['auth/login', 'auth/me', 'users', 'majors', 'subjects', 'classes', 'weekly-plans', 'attendance', 'pickup'];
  const isExcluded = excludedWords.some(word => error.url?.includes(word));
  let errorMessage = 'An unexpected error occurred';

  if (error.status === 0) {
    errorMessage = 'Connection to server has been lost!';
  } else if (error.status === 500 || error.status === 400) {
    errorMessage = error.error?.errorMessages?.[0]?.message || error.error?.error?.message || error.message;
  } else {
    errorMessage = error.error?.error?.message || error.message;
  }

  if (!isExcluded && !isDialogOpened) {
    const dialogRef = dialog.open(ErrorComponent, {
      width: '500px',
      disableClose: true,
      data: errorMessage,
      panelClass: 'confirmation-modal'
    });
    isDialogOpened = true;
    dialogRef.afterClosed().subscribe(() => {
      isDialogOpened = false;
    });
  }

  return throwError(() => error);
};

export const AppHttpInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const router = inject(Router);
  const dialog = inject(MatDialog);
  const snackBar = inject(MatSnackBar);
  const session_token = localStorage.getItem(AppConstants.SESSION_TOKEN);

  if (session_token !== null) {
    req = req.clone({ headers: req.headers.set('session_token', session_token) });
  }

  return next(req).pipe(
    map((event: HttpEvent<any>) => {
      if (event instanceof HttpResponse) {
        if (event.body && event.body.errorMessages &&
          event.body.errorMessages.some((msg: { code: number }) => msg.code === 401)) {
          throw new HttpErrorResponse({
            error: event.body,
            headers: event.headers,
            status: 401,
            statusText: 'Unauthorized',
            url: event.url || undefined
          });
        }
      }
      return event;
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 ||
        (error.error && error.error.errorMessages &&
          error.error.errorMessages.some((msg: { code: number }) => msg.code === 401))) {
        logout(router, snackBar);
        return throwError(() => error);
      }
      return handleError(error, dialog);
    })
  );
};
